import React, { useEffect, useRef, useState } from 'react';
import { styles } from './ProductService.css';
import { styles as swatchStyles } from '../variants/swatches/Swatches.css';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { LineItemProps, OfferingsProps, ServiceOffering, ServicesProps } from '@jbhi-fi/apis/src/services/types';
import { getCartLoading, setCartLoading } from '../../../../utils/legacyCart';
import { ProductServiceButton } from '@jbhi-fi/components/src/Attach/ProductServiceButton/ProductServiceButton';
import {
  IsAttachedService,
  getProductServiceVariantId,
  getSelectedServiceSuggestion,
  initPDPServiceSelections,
  isPdpServiceSelected,
  removePDPServiceSelection,
  removeSelectedServiceSuggestion,
  setPDPServiceSelection,
  setSelectedServiceSuggestion,
  containsExtras,
  serviceTypeId,
} from '../utils/product-service';
import { dataLayerPushServiceEvent, setMiniCartVisible } from '../utils/utils';
import { ServiceIds } from '../../../../utils/constants/services';
import { getServiceOfferings } from '../../../../utils/parseServiceOfferings';
import { getServiceOfferingsKey } from '../../../../utils/cart/services/ServiceOfferings';
import { addServiceOffering } from '../../../../utils/cart/addServiceOffering';
import { useMinicartUiContext } from '../../../../utils/cart/context/MinicartUiContext';
import { ServicesInfoBlock } from '@jbhi-fi/components/src/Attach/ServicesInfoBlock/ServicesInfoBlock';
import {
  ModalWrapper,
  ProductServiceModal,
} from '@jbhi-fi/components/src/Attach/ProductServiceModal/ProductServiceModal';
import { SuggestionsApiResponse } from '@jbhi-fi/apis/src/services/ServicesApiClient';
import {
  elementIds,
  generateProductServiceModalProps,
  generateServicesInfoBlockProps,
  getServiceButtonHeading,
} from './utils';
import { ServiceIcon } from './icons/ServiceIcon';
import { removeAttachedServiceOffering } from '../../../../utils/cart/removeAttachedServiceOffering';
import { useCartContext } from '../../../../utils/cart/context/CartContext';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { applyNoWrap } from '../../../../utils/noWrap';

export type ProductServiceProps = {
  shouldShowOffering?: boolean;
  onAdd?: (offeringId?: string) => Promise<void>;
  serviceType?: string;
  suggestion?: any;
  variant?: 'default' | 'pdp' | 'compare' | 'popupPDP' | 'popupCollection' | 'popupSearch' | 'slideout' | 'cart';
  productSku: string;
  productCartId?: string;
  preSelectedOfferingId?: string;
  gtmData: any;
  suggestions: SuggestionsApiResponse['lineItems'] | undefined;
  itemGapPx?: number;
  showServiceIds?: Array<ServiceIds>;
  isAddToCartPopUpVisible?: boolean;
  productCategoryHierarchy?: Array<string>;
  isColourOption?: boolean;
};

const ProductService = ({
  shouldShowOffering = true,
  onAdd,
  serviceType,
  variant = 'default',
  productSku,
  productCartId,
  preSelectedOfferingId,
  gtmData,
  suggestions,
  showServiceIds,
  isAddToCartPopUpVisible,
  isColourOption,
}: ProductServiceProps) => {
  const [currentSuggestion, setCurrentSuggestion] = useState<LineItemProps | undefined>(undefined);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selected, setSelected] = useState<boolean>(false);
  const [selectedModalId, setSelectedModalId] = useState<string>('');
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | undefined>(preSelectedOfferingId);
  const { isOpen: isMinicartOpen } = useMinicartUiContext();
  const { setProductServiceOfferId } = useShopifyProduct();
  const isPdpOrCompare = ['pdp', 'compare'].includes(variant);
  const gtmSource = variant !== 'default' ? variant : window?.gtm?.pageType === 'Checkout Pages' ? 'cart' : 'minicart';
  const isCartPage = gtmSource === 'cart';
  const isMiniCart = gtmSource === 'minicart';
  const cartType = isCartPage ? 'cart' : isMiniCart ? 'miniCart' : undefined;

  const service = currentSuggestion?.services?.find(service => service?.shouldRender);
  const { cart } = useCartContext();
  const [isCartReady, setIsCartReady] = useState<boolean>(true);

  const getCurrentServiceType = () => {
    if (serviceType) {
      return serviceType;
    }
    if (containsExtras(currentSuggestion?.services)) {
      return serviceTypeId;
    }
    if (currentSuggestion?.services && currentSuggestion.services[0]) {
      return currentSuggestion.services[0].id ?? '';
    }
    return '';
  };

  const popUpVariants = ['popupPDP', 'popupCollection', 'popupSearch'];

  const serviceElementIds = {
    productServicesWrapper: useIdPrefix('product-services-wrapper'),
  };

  useEffect(() => {
    if (productCartId) {
      setIsCartReady(false);
      if (cart?.items?.find(item => item.properties?._uuid === productCartId)) {
        setIsCartReady(true);
      }
    }
    if (preSelectedOfferingId && productCartId) {
      if (
        tryParseJson<ServiceOffering>(
          cart?.items?.find(item => IsAttachedService(item) && item.properties?._uuid === productCartId)?.properties
            ?._serviceData
        )?.id.includes(preSelectedOfferingId)
      ) {
        setIsCartReady(true);
      }
    }
  }, [productCartId, preSelectedOfferingId]);

  const prepareOfferingGtmData = (service: ServicesProps, offeringId?: string) => {
    const offerings = offeringId ? service?.offerings?.filter(o => o.id === offeringId) : null;
    const { sku, name, priceInc } = offerings?.[0] || service.offerings?.[0] || {};
    return { sku, name, priceInc };
  };

  const addServiceToExistingItem = async (serviceId: string, offeringId: string) => {
    if (variant !== 'default') {
      // Check if the product is already added to the cart and if it has attached service
      let productIndex = null;
      let productId = null;
      let primaryLineItem = null;
      cart.items?.forEach((item, itemIndex) => {
        if ((item.sku === productSku && !item.properties._serviceLink) || item.properties._serviceLink === '') {
          productIndex = itemIndex;
          productId = item.id;
          primaryLineItem = item;
        }
      });
      if (productIndex !== null && productId && primaryLineItem) {
        setIsCartReady(false);
        setCartLoading(true);
        const serviceOfferings = getServiceOfferings();
        if (!serviceOfferings) return null;
        const serviceOfferingsKey = getServiceOfferingsKey(primaryLineItem);
        //@ts-ignore
        const serviceOffering = (serviceOfferings?.[serviceOfferingsKey] as Array<ServiceOffering>)?.find(service => {
          return service.typeId === serviceId && service.id.includes(offeringId);
        });
        if (!serviceOffering || !serviceOffering.shouldRender) return;
        const { variantId, googleTagManager, sku, typeId, priceInc } = serviceOffering;

        let shopifyVariantId = variantId;
        if (typeId === ServiceIds.ExtraCare) {
          shopifyVariantId = await getProductServiceVariantId(sku, 'extra-care', priceInc);
        }
        setMiniCartVisible(isAddToCartPopUpVisible || variant == 'slideout' ? false : true);
        if (shopifyVariantId) {
          await addServiceOffering({
            variantId: shopifyVariantId,
            location: variant,
            googleTagManager,
            serviceData: serviceOffering,
            primaryLineItem: primaryLineItem,
          });
        }
        setIsCartReady(true);
      }
    }
  };

  const removeServiceFromExistingItem = async () => {
    const primaryItemServiceLink = cart?.items?.find(
      item => item.properties?._uuid === productCartId && item.sku === productSku && item.properties?._serviceLink
    )?.properties?._serviceLink;

    const attachedService = cart?.items?.find(
      item => IsAttachedService(item) && item.properties?._serviceLink === primaryItemServiceLink
    );

    const serviceData = tryParseJson<ServiceOffering>(attachedService?.properties?._serviceData);
    const lineId = attachedService?.line_id;

    if (attachedService && serviceData?.variantId && lineId) {
      setCartLoading(true);
      setIsCartReady(false);
      await removeAttachedServiceOffering(
        lineId,
        primaryItemServiceLink,
        serviceData.googleTagManager,
        variant,
        attachedService.isValidService
      );
      setIsCartReady(true);
    }
  };

  useEffect(() => {
    if (isPdpOrCompare) {
      initPDPServiceSelections();
    }

    if (!suggestions?.length) {
      setCurrentSuggestion(undefined);
      return;
    }
    setCurrentSuggestion(suggestions.find(li => li.productSku === productSku));
  }, [suggestions, productSku, isPdpOrCompare]);

  const onServiceSelected = async (service?: ServicesProps, offeringId?: string) => {
    if (getCartLoading()) return;
    if (variant !== 'default') {
      if (!service?.id || !offeringId) return;
      const updatedSuggestion = { ...currentSuggestion };
      updatedSuggestion.services = updatedSuggestion?.services?.filter(s => s.id === service?.id);

      const selectedOffering = service?.offerings?.find(offering => offering.id === selectedOfferingId);
      if (selectedOfferingId !== offeringId && selectedOffering) {
        await removeServiceFromExistingItem();
      }

      setSelected(false);
      setProductServiceOfferId('');

      if (variant === 'pdp') {
        removePDPServiceSelection();
        removeSelectedServiceSuggestion();
        setSelectedServiceSuggestion({ ...updatedSuggestion, selectedOfferingId: offeringId });
        setPDPServiceSelection(getCurrentServiceType(), productSku, true);
      }

      setSelected(true);
      setSelectedOfferingId(offeringId);
      setProductServiceOfferId(offeringId ?? '');
      if (service?.id && offeringId) {
        await addServiceToExistingItem(service.id, offeringId);
      }
      setModalVisible(false);
    } else {
      setSelected(true);
      await onAdd?.(offeringId);
    }

    if (service && offeringId && gtmData) {
      const { sku, name, priceInc } = prepareOfferingGtmData(service, offeringId);

      dataLayerPushServiceEvent(
        `${service?.id}.addedandconfirmed`,
        gtmData,
        { sku, name, servicePrice: priceInc },
        gtmSource
      );
    }
  };

  const checkServiceSelectedOnPDP = () => {
    const serviceSelected = isPdpServiceSelected(getCurrentServiceType(), productSku);
    if (isPdpOrCompare && serviceSelected) {
      const suggestion = getSelectedServiceSuggestion();
      if (!suggestion) {
        setSelectedServiceSuggestion({ ...currentSuggestion, selectedOfferingId });
      }
    }
    if (serviceSelected !== selected) setSelected(serviceSelected);
    return serviceSelected;
  };

  const onServiceDeselected = async (service?: ServicesProps, offeringId?: string, shouldRemoveFromCart?: boolean) => {
    removePDPServiceSelection();
    removeSelectedServiceSuggestion();
    const offering = service?.offerings?.find(offering => offering.id === offeringId);
    if (shouldRemoveFromCart && service?.id && offering) {
      await removeServiceFromExistingItem();
    }
    setSelected(false);
    setTimeout(() => setSelectedOfferingId(undefined), 200);
    setProductServiceOfferId('');
    if (service && offeringId && gtmData) {
      const { sku, name, priceInc } = prepareOfferingGtmData(service, offeringId);

      dataLayerPushServiceEvent(
        `${service.id}.deselected`,
        gtmData,
        {
          sku,
          name,
          priceInc,
        },
        variant
      );
    }
    setModalVisible(false);
  };

  const isSmallScreen = () => {
    const { innerWidth } = window;
    return innerWidth < 1400;
  };

  const onButtonClick = (event: React.ChangeEvent | React.SyntheticEvent, offering?: OfferingsProps) => {
    event.preventDefault();
    setModalVisible(!modalVisible);
    if (offering) {
      setSelectedModalId(offering.id);
      dataLayerPushServiceEvent(
        `${service?.id}.clickextracarebutton`,
        gtmData,
        { sku: offering.sku, name: offering.name, price: offering.priceInc },
        gtmSource
      );
      if (isSmallScreen() && !isPdpOrCompare) {
        setMiniCartVisible(false);
      }
    }
  };

  const onModalClose = (event: React.ChangeEvent | React.SyntheticEvent | KeyboardEvent, offering: OfferingsProps) => {
    setModalVisible(false);
    gtmData?.product
      ? dataLayerPushServiceEvent(
          `${getCurrentServiceType()}.closemodal`,
          gtmData.product,
          {
            sku: offering.sku,
            name: offering.name,
            price: offering.priceInc,
          },
          gtmData.source
        )
      : null;
  };

  const trackButtonImpression = ({ serviceId, offering }: { serviceId: string; offering?: OfferingsProps }) => {
    const trackGtmButtonImpression = () => {
      if (offering && gtmData) {
        dataLayerPushServiceEvent(
          `${serviceId}.button.impression`,
          gtmData.product,
          { sku: offering.sku, name: offering.name, price: offering.priceInc },
          gtmData.source
        );
      }
    };

    if (gtmData?.source === 'minicart' && isMinicartOpen) {
      trackGtmButtonImpression();
    }
    if (gtmData?.source === 'pdp' && !isMinicartOpen) {
      trackGtmButtonImpression();
    }
    if (gtmData?.source === 'popup' && isAddToCartPopUpVisible) {
      trackGtmButtonImpression();
    }
  };

  useEffect(() => {
    if (isPdpOrCompare) {
      // @ts-ignore
      window.addEventListener('JB-pdp-service-deselect', onServiceDeselected);
      return () => {
        // @ts-ignore
        window.removeEventListener('JB-pdp-service-deselect', onServiceDeselected);
      };
    }
  }, []);

  useEffect(() => {
    if (modalVisible) {
      if (modalContainerRef.current) {
        const modalElement = modalContainerRef?.current?.children?.[0].children?.[0] as HTMLDivElement | undefined;
        if (modalElement) {
          modalElement.focus();
          modalElement.click();
        }
      }
    }
  }, [modalVisible]);

  useEffect(() => {
    trackButtonImpression({
      serviceId: service?.id ?? '',
      offering: service?.offerings?.find(offering => offering.id === selectedOfferingId),
    });
  }, [isMinicartOpen, isAddToCartPopUpVisible]);

  if (
    !shouldShowOffering ||
    !suggestions?.length ||
    !getCurrentServiceType() ||
    !currentSuggestion ||
    !currentSuggestion.services.length ||
    (showServiceIds && !showServiceIds.includes(getCurrentServiceType() as ServiceIds))
  ) {
    if (variant === 'compare') {
      return <>-</>;
    }
    return null;
  }

  if (!service || !service.offerings?.length) return <></>;

  if (isPdpOrCompare && checkServiceSelectedOnPDP() && !selected) {
    setSelected(true);
  }
  const isSingleOffer = service.offerings.length === 1 && !isCartPage;

  const isExtraCareReplacementCover = currentSuggestion.services.some(service =>
    service.offerings?.some(offering => offering.sku === '7800500')
  );
  const isExtraCare = [ServiceIds.ExtraCare, ServiceIds.ExtraCareNz].includes(getCurrentServiceType() as ServiceIds);
  const isAppleCareNz = getCurrentServiceType() === ServiceIds.AppleCareNz;

  const serviceOfferings =
    service?.offerings?.map((offering, i) => (
      <div className={styles.productOffer({ isSingleOffer, page: cartType })} key={`offering-${offering.id}`}>
        <ModalWrapper
          key={i}
          onClose={e => onModalClose(e, offering)}
          isModalOpen={modalVisible ? selectedModalId === offering.id : false}
          modalDomElement={document.getElementsByClassName('body-container')?.[0]}
        >
          <ProductServiceModal
            {...generateProductServiceModalProps({
              service,
              offering,
              onAdd: onServiceSelected,
              onRemove: onServiceDeselected,
              selectedOfferingId,
              isExtraCare,
              isExtraCareReplacementCover,
              isAppleCareNz,
              isCartReady,
              preSelectedOfferingId,
            })}
          />
        </ModalWrapper>
        <ProductServiceButton
          onButtonClick={event => onButtonClick(event, offering)}
          selected={selected ? selectedOfferingId === offering.id : false}
          isCompare={variant === 'compare'}
          heading={getServiceButtonHeading(offering, variant === 'compare')}
          price={offering.priceInc}
          elementId={elementIds[offering.id as ServiceIds]}
          offeringId={offering.id}
          icon={
            <ServiceIcon imageUrl={service.imageUrl ?? offering.imageUrl ?? undefined} selected={selected} size={42} />
          }
        />
      </div>
    )) ?? [];

  const titleText = (() => {
    if (isAppleCareNz) {
      return window.Jbhifi?.attachConfig?.servicesAppleCareNzHeading;
    }
    if (isExtraCare) {
      return window.Jbhifi?.attachConfig?.servicesExtraCareHeading;
    }
    return applyNoWrap(window.Jbhifi?.attachConfig?.servicesJBExtrasHeading, ['JB Hi-Fi']);
  })();

  const servicesInfoBlockProps = generateServicesInfoBlockProps({
    heading: titleText,
    onAdd: onButtonClick,
    onRemove: onServiceDeselected,
    service,
    isExtraCareReplacementCover,
    isExtraCare,
    isAppleCareNz,
    selectedOfferingId,
    buttonPosition: ['slideout', ...popUpVariants].includes(variant) ? 'right' : 'bottom',
    isSlideoutOrCart: ['slideout', 'cart'].includes(variant),
    isCartReady,
  });

  if (variant === 'compare') {
    return <>{serviceOfferings}</>;
  }
  if (variant === 'cart') {
    return (
      <ProductServiceModal
        {...generateProductServiceModalProps({
          service,
          offering: service.offerings?.[0],
          onAdd: onServiceSelected,
          onRemove: onServiceDeselected,
          selectedOfferingId,
          isExtraCare,
          isExtraCareReplacementCover,
          isAppleCareNz,
          isInlineModal: true,
          isSlideoutOrCart: true,
          isCartReady,
          preSelectedOfferingId,
        })}
      />
    );
  }
  if (['pdp', 'slideout', ...popUpVariants].includes(variant)) {
    const serviceInfoBlockType = servicesInfoBlockProps?.heading?.toLowerCase().replace(/\s/g, '_');
    return (
      <div
        id={serviceElementIds.productServicesWrapper}
        data-service-block-type={serviceInfoBlockType ?? 'standard'}
        className={
          variant === 'slideout' ? '' : `${swatchStyles.container({ isColourOption })} container-productServices`
        }
      >
        {servicesInfoBlockProps ? <ServicesInfoBlock {...servicesInfoBlockProps} /> : null}
        {(service.offerings as Array<OfferingsProps>)?.map((offering, i) => (
          <ModalWrapper
            key={i}
            onClose={e => onModalClose(e, offering)}
            isModalOpen={modalVisible ? selectedModalId === offering.id : false}
            modalDomElement={document.getElementsByClassName('body-container')?.[0]}
          >
            <ProductServiceModal
              {...generateProductServiceModalProps({
                service,
                offering,
                onAdd: onServiceSelected,
                onRemove: onServiceDeselected,
                selectedOfferingId,
                isExtraCare,
                isExtraCareReplacementCover,
                isAppleCareNz,
                isSlideoutOrCart: variant === 'slideout',
                isCartReady,
                preSelectedOfferingId,
              })}
            />
          </ModalWrapper>
        )) ?? null}
      </div>
    );
  }
  if (variant === 'default') {
    return (
      <>
        <CalloutFont as="h3" id="service-title" className={styles.addExtendedWarrantyWords({ page: cartType })}>
          <span dangerouslySetInnerHTML={{ __html: titleText }} />
        </CalloutFont>
        {!isExtraCare && !isAppleCareNz ? <span>{service.offerings[0]?.shortDescription}</span> : null}
        <div className={`${styles.productOffersWrap} ${isCartPage ? 'isCartPage' : ''}`}>{serviceOfferings}</div>
      </>
    );
  }
  return null;
};

export default ProductService;
